import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard, Trash2, CheckCircle, ScrollText,
  BarChart3, ChevronLeft, ChevronRight, RefreshCw
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../hooks/useAuth";
import { supabase } from "../../lib/supabaseClient";

const NAV = [
  { label: "Dashboard",     icon: LayoutDashboard, path: "/hoa/dashboard",    active: true },
  { label: "Submissions",   icon: Trash2,           path: "/hoa/submissions" },
  { label: "Valet Status",  icon: CheckCircle,      path: "/hoa/valet-status" },
  { label: "Activity Logs", icon: ScrollText,       path: "/hoa/logs" },
  { label: "Reports",       icon: BarChart3,        path: "/hoa/reports" },
];


const PAGE_SIZE = 25;

function getToday() { return new Date().toISOString().split("T")[0]; }
// function addDays(dateStr, n) {
//   const d = new Date(dateStr + "T00:00:00");
//   d.setDate(d.getDate() + n);
//   return d.toISOString().split("T")[0];
// }
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d); // local time, no timezone shift
  date.setDate(date.getDate() + n);
  const yy = date.getFullYear();
  const mm  = String(date.getMonth() + 1).padStart(2, "0");
  const dd  = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
function formatDate(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function DateNavigator({ date, onChange }) {
  const isToday = date === getToday();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button onClick={() => onChange(addDays(date, -1))} style={{ width: 32, height: 32, borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ChevronLeft size={16} color="var(--mut)" />
      </button>
      <div style={{ padding: "6px 16px", borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", fontSize: 13, fontWeight: 600, color: "var(--ink)", minWidth: 150, textAlign: "center" }}>
        {isToday ? `Today · ${formatDate(date)}` : formatDate(date)}
      </div>
      <button onClick={() => onChange(addDays(date, 1))} disabled={isToday} style={{ width: 32, height: 32, borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: isToday ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: isToday ? 0.4 : 1 }}>
        <ChevronRight size={16} color="var(--mut)" />
      </button>
      {!isToday && (
        <button onClick={() => onChange(getToday())} style={{ padding: "6px 12px", borderRadius: 8, border: "1.5px solid var(--g300)", background: "var(--g50)", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "var(--g600)" }}>Today</button>
      )}
    </div>
  );
}

export default function HOASubmissions() {
  const { profile } = useAuth();
  const propertyId  = profile?.property_id;
  const today       = getToday();

  const [submissions,   setSubmissions]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [total,         setTotal]         = useState(0);
  const [page,          setPage]          = useState(0);
  const [selectedDate,  setSelectedDate]  = useState(today);
  const [totalUnits,    setTotalUnits]    = useState(0);
  const [searchUnit,    setSearchUnit]    = useState("");

  // Fetch total units
  useEffect(() => {
    if (!propertyId) return;
    supabase.from("properties").select("total_units").eq("id", propertyId).maybeSingle()
      .then(({ data }) => setTotalUnits(data?.total_units || 0));
  }, [propertyId]);

  const fetchSubmissions = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);

    let query = supabase
      .from("activity_logs")
      .select("id, user_id, metadata, created_at", { count: "exact" })
      .eq("property_id", propertyId)
      .eq("action", "resident_trash_submitted")
      .gte("created_at", `${selectedDate}T00:00:00`)
      .lte("created_at", `${selectedDate}T23:59:59`)
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    const { data, count } = await query;

    // Filter by unit search
    let filtered = data || [];
    if (searchUnit.trim()) {
      filtered = filtered.filter(s =>
        String(s.metadata?.unit_number || "").includes(searchUnit.trim())
      );
    }

    setSubmissions(filtered);
    setTotal(count || 0);
    setLoading(false);
  }, [propertyId, selectedDate, page, searchUnit]);

  useEffect(() => { setPage(0); }, [selectedDate, searchUnit]);
  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const totalPages    = Math.ceil(total / PAGE_SIZE);
  const submittedCount = total;
  const pendingCount   = Math.max(0, totalUnits - submittedCount);
  const pct            = totalUnits > 0 ? Math.round((submittedCount / totalUnits) * 100) : 0;

  return (
    <DashboardLayout role="hoa_admin" title="Submissions" navItems={NAV}>

      {/* Header */}
      <div className="fu" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "#1565c0", marginBottom: 6 }}>HOA Admin</div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 500, color: "var(--ink)", margin: 0 }}>Submissions</h2>
            <p style={{ fontSize: 13, color: "var(--mut)", marginTop: 4 }}>Resident trash confirmations for your property.</p>
          </div>
          <DateNavigator date={selectedDate} onChange={setSelectedDate} />
        </div>
      </div>

      {/* Summary cards */}
      <div className="fu d1" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Submitted",  value: submittedCount, color: "var(--g600)", bg: "var(--g50)",  border: "var(--g200)" },
          { label: "Pending",    value: pendingCount,   color: "#e65100",     bg: "#fff3e0",     border: "#ffb74d" },
          { label: "Completion", value: `${pct}%`,      color: "#1565c0",     bg: "#e3f2fd",     border: "#90caf9" },
        ].map(({ label, value, color, bg, border }) => (
          <div key={label} style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 12, padding: "14px", textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 700, color, fontFamily: "'Outfit',sans-serif", lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: ".5px", marginTop: 4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="fu d2" style={{ background: "#fff", borderRadius: 12, border: "1px solid var(--ln)", padding: "14px 18px", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12, fontWeight: 600, color: "var(--mut)" }}>
          <span>Submission Progress</span>
          <span>{submittedCount} of {totalUnits} units</span>
        </div>
        <div style={{ height: 8, borderRadius: 99, background: "#f0f0f0", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: "var(--g600)", borderRadius: 99, transition: "width .4s ease" }} />
        </div>
      </div>

      {/* Search + Refresh */}
      <div className="fu d3" style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
        <input
          value={searchUnit} onChange={e => setSearchUnit(e.target.value)}
          placeholder="Filter by unit number…"
          style={{ flex: 1, padding: "9px 13px", borderRadius: 8, fontSize: 13, border: "1.5px solid var(--ln)", background: "#fafafa", outline: "none", color: "var(--ink)" }}
        />
        <button onClick={fetchSubmissions} style={{ width: 36, height: 36, borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <RefreshCw size={14} color="var(--mut)" />
        </button>
        <div style={{ fontSize: 12, color: "var(--mut)", fontWeight: 600, whiteSpace: "nowrap" }}>{total} total</div>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "var(--mut)", fontSize: 13 }}>Loading…</div>
      ) : submissions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "36px", background: "#fff", borderRadius: 14, border: "1.5px dashed var(--ln)" }}>
          <Trash2 size={28} color="var(--ln)" style={{ marginBottom: 10 }} />
          <div style={{ fontSize: 13, color: "var(--mut)" }}>No submissions {selectedDate === today ? "today" : "on this date"}.</div>
        </div>
      ) : (
        submissions.map((s, i) => (
          <div key={s.id} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 16px", borderRadius: 11,
            background: "#fff", border: "1px solid var(--ln)",
            marginBottom: 8, boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--g50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Trash2 size={14} color="var(--g600)" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>
                Unit #{s.metadata?.unit_number ?? "—"}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--mut)", marginTop: 2 }}>
                Submitted at {formatTime(s.created_at)}
              </div>
            </div>
            {i === 0 && selectedDate === today && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "var(--g600)", color: "#fff", textTransform: "uppercase", letterSpacing: ".5px" }}>Latest</span>
            )}
          </div>
        ))
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: page === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, color: "var(--mut)", opacity: page === 0 ? 0.4 : 1 }}>← Prev</button>
          <span style={{ fontSize: 13, color: "var(--mut)", fontWeight: 600 }}>Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: page >= totalPages - 1 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, color: "var(--mut)", opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next →</button>
        </div>
      )}

      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </DashboardLayout>
  );
}